/**
 * Config-driven required / visible resolution for the four core consumer
 * contact fields in the registration wizard (email / first_name / last_name /
 * phone_number).
 *
 * The wizard renders these fields under its own front-end code_names
 * (`consumerEmail`, ...), but their per-tenant `required_when` / `visible_when`
 * rules are authored as `configuration_data` on the BACKEND consumer attributes,
 * keyed by the backend code_names (`email`, `phone_number`, ...). This module
 * bridges the two: it sources each field's `configuration_data` from the
 * holding-form-schema `consumer_fields` (emitted when the backend runs with
 * `include_core_contact=True`) and evaluates it against a backend-keyed value
 * map via the shared `effectiveRequired` / `isFieldVisible` helpers - the exact
 * same evaluation the generated form path and the backend capture gate use. So
 * the wizard honours e.g. the WPS-118 phone-or-email rule with zero bespoke
 * cross-field logic.
 *
 * When the backend has not injected core-contact config (still dormant, or the
 * schema is loading), the config map is empty and both helpers fall back to the
 * supplied base flags - i.e. the wizard behaves exactly as it did before.
 */
import { effectiveRequired, isFieldVisible } from "@/shared/lib/forms";

/** Wizard front-end code_name -> backend consumer attribute code_name. */
export const CONTACT_FE_TO_BACKEND: Record<string, string> = {
  consumerEmail: "email",
  consumerFirstName: "first_name",
  consumerLastName: "last_name",
  consumerPhone: "phone_number",
};

interface ConsumerFieldMetaLike {
  code_name?: string;
  configuration_data?: Record<string, unknown> | null;
}

/**
 * Map of backend code_name -> `configuration_data` for the core contact fields,
 * sourced from a holding-form-schema `consumer_fields` list. Empty when absent.
 */
export function coreContactConfigMap(
  consumerFields: ConsumerFieldMetaLike[] | null | undefined,
): Record<string, Record<string, unknown> | undefined> {
  const backendCodes = new Set(Object.values(CONTACT_FE_TO_BACKEND));
  const out: Record<string, Record<string, unknown> | undefined> = {};
  for (const f of consumerFields ?? []) {
    if (f?.code_name && backendCodes.has(f.code_name)) {
      out[f.code_name] = (f.configuration_data ?? undefined) as
        Record<string, unknown> | undefined;
    }
  }
  return out;
}

/**
 * Backend-code_name-keyed value map for the four contact fields, with blanks
 * normalised to `''` so a `required_when: { phone_number: [''] }` rule matches
 * an absent / whitespace-only sibling (the helpers match on strict membership,
 * where a missing sibling reads as `undefined`, not `''`).
 */
export function contactBackendValues(v: {
  consumerEmail?: string | null;
  consumerFirstName?: string | null;
  consumerLastName?: string | null;
  consumerPhone?: string | null;
}): Record<string, string> {
  return {
    email: (v.consumerEmail ?? "").trim(),
    first_name: (v.consumerFirstName ?? "").trim(),
    last_name: (v.consumerLastName ?? "").trim(),
    phone_number: (v.consumerPhone ?? "").trim(),
  };
}

export interface ResolvedContactFlags {
  required: boolean;
  visible: boolean;
}

/**
 * Resolve a contact field's effective `required` / `visible`, layering any
 * `configuration_data` `required_when` / `visible_when` (from `configMap`) on
 * top of the wizard's base flags. With no config for the field, returns the
 * base flags unchanged.
 */
export function resolveContactField(
  feCodeName: string,
  configMap: Record<string, Record<string, unknown> | undefined>,
  backendValues: Record<string, string>,
  baseRequired: boolean,
  baseVisible: boolean,
): ResolvedContactFlags {
  const backendCode = CONTACT_FE_TO_BACKEND[feCodeName];
  const config = backendCode ? configMap[backendCode] : undefined;
  return {
    required: effectiveRequired(config, backendValues, baseRequired),
    visible: baseVisible && isFieldVisible(config, backendValues),
  };
}
